import React, { createContext, useContext, useState, useEffect } from 'react';

const CartContext = createContext();

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within CartProvider');
  }
  return context;
};

// Preorder discount percentage
const PREORDER_DISCOUNT = 0.10; // 10%
const PREORDER_DEPOSIT = 0.20; // 20% Anzahlung

export const CartProvider = ({ children }) => {
  const [cart, setCart] = useState([]);

  // Load cart from localStorage on mount
  useEffect(() => {
    const savedCart = localStorage.getItem('jannesmann_cart');
    if (savedCart) {
      setCart(JSON.parse(savedCart));
    }
  }, []);

  // Save cart to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('jannesmann_cart', JSON.stringify(cart));
  }, [cart]);

  // Check if cart has preorder items
  const hasPreorderItems = () => {
    return cart.some(item => item.isPreorder === true);
  };

  // Check if cart has regular (non-preorder) items
  const hasRegularItems = () => {
    return cart.some(item => item.isPreorder !== true);
  };

  // Check if adding an item would create a mixed cart
  const canAddItem = (isPreorder) => {
    if (cart.length === 0) return true;
    
    // If cart has preorder items and trying to add regular item -> not allowed
    if (hasPreorderItems() && !isPreorder) return false;
    
    // If cart has regular items and trying to add preorder item -> not allowed
    if (hasRegularItems() && isPreorder) return false;
    
    return true;
  };

  const addToCart = (product, quantity = 1, isPreorder = false) => {
    // Check for mixed cart
    if (!canAddItem(isPreorder)) {
      return { 
        success: false, 
        error: isPreorder 
          ? 'Sie haben bereits Artikel im Warenkorb. Bitte schließen Sie zuerst Ihren aktuellen Einkauf ab, bevor Sie vorbestellen.'
          : 'Sie haben bereits Vorbestellungen im Warenkorb. Bitte schließen Sie zuerst Ihre Vorbestellung ab.'
      };
    }

    setCart(currentCart => {
      // For preorder, create a unique ID
      const cartItemId = isPreorder ? `${product.id}_preorder` : product.id;
      const existingItem = currentCart.find(item => 
        isPreorder ? item.id === cartItemId : (item.id === product.id && !item.isPreorder)
      );
      
      if (existingItem) {
        // Update quantity
        return currentCart.map(item =>
          item.id === cartItemId
            ? { ...item, quantity: item.quantity + quantity }
            : item
        );
      } else {
        // Calculate preorder prices if applicable
        const basePrice = product.pricing?.numericPrice || product.price || 0;
        const preorderPrice = isPreorder ? basePrice * (1 - PREORDER_DISCOUNT) : basePrice;
        const depositAmount = isPreorder ? preorderPrice * PREORDER_DEPOSIT : null;
        const remainingAmount = isPreorder ? preorderPrice - depositAmount : null;
        
        // Add new item
        return [...currentCart, { 
          ...product, 
          id: cartItemId,
          originalId: product.id, // Keep original product ID
          quantity,
          isPreorder,
          // Preorder specific fields
          originalPrice: isPreorder ? basePrice : null,
          preorderDiscount: isPreorder ? PREORDER_DISCOUNT : null,
          preorderPrice: isPreorder ? preorderPrice : null,
          depositAmount: depositAmount,
          remainingAmount: remainingAmount
        }];
      }
    });
    
    return { success: true };
  };

  const removeFromCart = (productId) => {
    setCart(currentCart => currentCart.filter(item => item.id !== productId));
  };

  const updateQuantity = (productId, quantity) => {
    if (quantity <= 0) {
      removeFromCart(productId);
      return;
    }
    
    setCart(currentCart =>
      currentCart.map(item => {
        if (item.id === productId) {
          // Recalculate preorder amounts if applicable
          if (item.isPreorder) {
            const basePrice = item.originalPrice || item.pricing?.numericPrice || item.price || 0;
            const preorderPrice = basePrice * (1 - PREORDER_DISCOUNT);
            return { 
              ...item, 
              quantity,
              preorderPrice,
              depositAmount: preorderPrice * PREORDER_DEPOSIT,
              remainingAmount: preorderPrice * (1 - PREORDER_DEPOSIT)
            };
          }
          return { ...item, quantity };
        }
        return item;
      })
    );
  };

  const clearCart = () => {
    setCart([]);
  };

  const getCartTotal = () => {
    return cart.reduce((total, item) => {
      // For preorder items, use deposit amount; for regular items, use full price
      if (item.isPreorder) {
        return total + (item.depositAmount * item.quantity);
      }
      const price = item.pricing?.numericPrice || item.price || 0;
      return total + (price * item.quantity);
    }, 0);
  };

  // Get the full value of items (before preorder deposit calculation)
  const getCartFullValue = () => {
    return cart.reduce((total, item) => {
      if (item.isPreorder) {
        return total + (item.preorderPrice * item.quantity);
      }
      const price = item.pricing?.numericPrice || item.price || 0;
      return total + (price * item.quantity);
    }, 0);
  };

  // Get total original value (before any discounts)
  const getCartOriginalValue = () => {
    return cart.reduce((total, item) => {
      if (item.isPreorder) {
        return total + (item.originalPrice * item.quantity);
      }
      const price = item.pricing?.numericPrice || item.price || 0;
      return total + (price * item.quantity);
    }, 0);
  };

  // Get total discount amount
  const getTotalDiscount = () => {
    return cart.reduce((total, item) => {
      if (item.isPreorder) {
        return total + ((item.originalPrice - item.preorderPrice) * item.quantity);
      }
      return total;
    }, 0);
  };

  // Get total remaining amount (for preorders)
  const getTotalRemainingAmount = () => {
    return cart.reduce((total, item) => {
      if (item.isPreorder) {
        return total + (item.remainingAmount * item.quantity);
      }
      return total;
    }, 0);
  };

  const getCartCount = () => {
    return cart.reduce((count, item) => count + item.quantity, 0);
  };

  const getTaxAmount = () => {
    return getCartTotal() * 0.19;
  };

  const getGrandTotal = () => {
    return getCartTotal() + getTaxAmount();
  };

  const value = {
    cart,
    setCart,
    addToCart,
    removeFromCart,
    updateQuantity,
    clearCart,
    getCartTotal,
    getCartFullValue,
    getCartOriginalValue,
    getTotalDiscount,
    getTotalRemainingAmount,
    getCartCount,
    getTaxAmount,
    getGrandTotal,
    hasPreorderItems,
    hasRegularItems,
    canAddItem,
    cartCount: getCartCount(),
    isPreorderCart: hasPreorderItems(),
    PREORDER_DISCOUNT,
    PREORDER_DEPOSIT
  };

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};